import React, { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import aiIcon from 'src/assets/icons/ai.svg'
import { cn } from 'src/helpers/style'
import i18n from 'src/i18n'

import {
  AppIcon,
  AppIconNames,
  Button,
  ButtonIconPosition,
  ButtonSize,
  ButtonVariant,
  Typography,
  TypographyVariant,
  TypographyWeight,
} from 'src/components/atoms'
import { DefaultDialog } from 'src/components/molecules/dialog/default-dialog'

import { AskAiPanel, useAskAiPanel } from 'src/app/ask-ai'

const ASK_AI_PANEL_ID = 'ask-ai-panel'

const headerInteractiveLinkBaseClassName =
  'inline-flex min-h-0 items-center no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-page'

export const headerBrandLinkClassName = headerInteractiveLinkBaseClassName

/** Shell for header nav tabs; pair with `Typography` `labelMd` + light weight. */
export function headerNavTabClassName(isActive: boolean): string {
  return cn(
    headerInteractiveLinkBaseClassName,
    'justify-center rounded-2xl px-3 py-1.5 leading-normal whitespace-nowrap transition-colors duration-200',
    isActive
      ? 'bg-content-primary text-surface-page hover:bg-content-primary hover:text-surface-page'
      : 'bg-transparent hover:bg-content-primary hover:text-surface-page',
  )
}

const headerRowClassName =
  'flex h-[68px] items-center justify-between px-5 md:h-[81px] md:px-12 xl:px-[120px]'

const headerIconButtonClassName =
  'inline-flex h-8 w-8 items-center justify-center rounded-full text-content-primary transition-colors hover:bg-state-hover-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-page'

type IconButtonVisibility = 'mobileOnly' | 'desktopOnly'

interface IHeaderIconButtonProps {
  iconName: AppIconNames
  ariaLabel: string
  iconSize?: number
  visibility?: IconButtonVisibility
  onClick?: () => void
}

const visibilityClassName: Record<IconButtonVisibility, string> = {
  mobileOnly: 'md:hidden',
  desktopOnly: 'hidden md:inline-flex',
}

const HeaderIconButton = ({
  iconName,
  ariaLabel,
  iconSize = 16,
  visibility,
  onClick,
}: IHeaderIconButtonProps): JSX.Element => (
  <button
    type="button"
    aria-label={ariaLabel}
    onClick={onClick}
    className={cn(
      headerIconButtonClassName,
      visibility ? visibilityClassName[visibility] : undefined,
      visibility === 'desktopOnly' && 'border border-border-default',
    )}
  >
    <AppIcon name={iconName} size={iconSize} color="currentColor" />
  </button>
)

interface IHeaderAskAiButtonProps {
  onClick: () => void
  isOpen: boolean
}

const HeaderAskAiButton = ({
  onClick,
  isOpen,
}: IHeaderAskAiButtonProps): JSX.Element => (
  <Button
    type="button"
    variant={ButtonVariant.askAi}
    size={ButtonSize.medium}
    label={i18n.t('header.askAi')}
    aria-label={i18n.t('header.askAi')}
    aria-expanded={isOpen}
    aria-controls={ASK_AI_PANEL_ID}
    onClick={onClick}
    className="min-h-0 shrink-0"
    icon={
      <img
        src={aiIcon}
        alt={i18n.t('header.askAi')}
        aria-hidden
        className="size-4 shrink-0"
      />
    }
  />
)

const DefaultBrand = (
  <Typography
    variant={TypographyVariant.titleSm}
    weight={TypographyWeight.medium}
    className="whitespace-nowrap text-brand-accent"
  >
    {i18n.t('brand.name')}
  </Typography>
)

interface IMobileMenuDrawerProps {
  isOpen: boolean
  onClose: () => void
  links?: React.ReactNode
  isAuthenticated: boolean
  onLogout: () => void
}

const MobileMenuDrawer = ({
  isOpen,
  onClose,
  links,
  isAuthenticated,
  onLogout,
}: IMobileMenuDrawerProps): JSX.Element => {
  const { t } = useTranslation()
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect((): (() => void) | undefined => {
    if (!isOpen) return undefined
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return (): void => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  useEffect((): void => {
    if (isOpen) closeButtonRef.current?.focus()
  }, [isOpen])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('header.mobileNav')}
      aria-hidden={!isOpen}
      className={cn(
        'fixed inset-0 z-50 flex flex-col bg-surface-page transition-all duration-200 ease-out md:hidden',
        isOpen
          ? 'pointer-events-auto translate-y-0 opacity-100'
          : 'pointer-events-none -translate-y-2 opacity-0',
      )}
    >
      <div className="flex h-[68px] items-center justify-end px-5">
        <button
          ref={closeButtonRef}
          type="button"
          aria-label={t('header.closeMenu')}
          onClick={onClose}
          className={headerIconButtonClassName}
        >
          <AppIcon name={AppIconNames.close} size={24} color="currentColor" />
        </button>
      </div>

      {links && (
        <nav
          aria-label={t('header.mobileNav')}
          className="border-t border-border-default"
        >
          {links}
        </nav>
      )}

      {isAuthenticated && (
        <div className="px-5 py-6">
          <Button
            type="button"
            label={t('auth.logout.logout')}
            variant={ButtonVariant.primary}
            size={ButtonSize.medium}
            iconPosition={ButtonIconPosition.right}
            icon={
              <AppIcon
                name={AppIconNames.signOut}
                size={16}
                color="currentColor"
              />
            }
            onClick={onLogout}
            className="w-full justify-center"
          />
        </div>
      )}
    </div>
  )
}

interface IHeaderProps {
  isAuthenticated: () => boolean
  onSignOut: () => Promise<void>
  children: React.ReactNode
  links?: React.ReactNode
  mobileMenuLinks?: (onClose: () => void) => React.ReactNode
  brand?: React.ReactNode
}

export const Header = ({
  isAuthenticated,
  onSignOut,
  links,
  mobileMenuLinks,
  brand,
  children,
}: IHeaderProps): JSX.Element => {
  const { t } = useTranslation()
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false)
  const [isSigningOut, setIsSigningOut] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const askAi = useAskAiPanel()

  const openMobileMenu = (): void => setIsMobileMenuOpen(true)
  const closeMobileMenu = (): void => setIsMobileMenuOpen(false)

  const openLogoutDialog = (): void => {
    closeMobileMenu()
    setIsLogoutDialogOpen(true)
  }

  useEffect((): (() => void) => {
    document.body.classList.toggle('overflow-hidden', isMobileMenuOpen)
    return (): void => {
      document.body.classList.remove('overflow-hidden')
    }
  }, [isMobileMenuOpen])

  const closeLogoutDialog = (): void => {
    if (isSigningOut) return
    setIsLogoutDialogOpen(false)
  }

  const handleConfirmSignOut = async (): Promise<void> => {
    setIsSigningOut(true)
    try {
      await onSignOut()
    } catch {
      // Sign-out recovery and user feedback belong to the route-level caller.
      // Keep a rejected callback from escaping this fire-and-forget click handler.
    } finally {
      setIsLogoutDialogOpen(false)
      setIsSigningOut(false)
    }
  }

  const authActions = (): React.ReactNode => {
    if (!isAuthenticated()) {
      return null
    }

    return (
      <Button
        type="button"
        variant={ButtonVariant.secondary}
        size={ButtonSize.medium}
        aria-label={t('auth.logout.logout')}
        icon={
          <AppIcon
            name={AppIconNames.signOut}
            size={16}
            alt={t('auth.logout.logout')}
          />
        }
        onClick={openLogoutDialog}
        className={cn('shrink-0', visibilityClassName.desktopOnly)}
      />
    )
  }

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-border-default bg-surface-page">
        <div className={headerRowClassName}>
          <div className="flex min-w-0 shrink-0 items-center gap-2 md:gap-0">
            <HeaderIconButton
              iconName={AppIconNames.menu}
              ariaLabel={t('header.openMenu')}
              iconSize={24}
              visibility="mobileOnly"
              onClick={openMobileMenu}
            />
            {brand ?? DefaultBrand}
          </div>

          {links ? (
            <div className="hidden min-h-0 min-w-0 flex-1 items-center justify-center px-4 md:flex">
              {links}
            </div>
          ) : (
            <div className="hidden flex-1 md:block" />
          )}

          <div className="flex shrink-0 items-center justify-end gap-2">
            <HeaderAskAiButton onClick={askAi.toggle} isOpen={askAi.isOpen} />
            {authActions()}
          </div>
        </div>
      </header>

      <AskAiPanel
        id={ASK_AI_PANEL_ID}
        isOpen={askAi.isOpen}
        isExpanded={askAi.isExpanded}
        onClose={askAi.close}
        onToggleExpand={askAi.toggleExpand}
      />

      <MobileMenuDrawer
        isOpen={isMobileMenuOpen}
        onClose={closeMobileMenu}
        links={mobileMenuLinks?.(closeMobileMenu)}
        isAuthenticated={isAuthenticated()}
        onLogout={openLogoutDialog}
      />

      <DefaultDialog
        isOpen={isLogoutDialogOpen}
        onClose={closeLogoutDialog}
        contentClassName="w-[400px] max-w-[calc(100vw-64px)] bg-surface-page rounded-[20px] p-8 shadow-md focus:outline-none"
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <Typography
              variant={TypographyVariant.titleSm}
              weight={TypographyWeight.light}
              className="text-content-primary"
              asChild
            >
              <h2>{t('auth.logout.title')}</h2>
            </Typography>
            <Typography
              variant={TypographyVariant.bodyXxs}
              className="leading-[1.55] text-content-secondary"
            >
              {t('auth.logout.description')}
            </Typography>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              label={t('auth.logout.cancel')}
              size={ButtonSize.medium}
              variant={ButtonVariant.secondary}
              onClick={closeLogoutDialog}
              disabled={isSigningOut}
              className="h-8 font-inter px-5 font-medium leading-none"
            />
            <Button
              type="button"
              label={t('auth.logout.logout')}
              size={ButtonSize.small}
              variant={ButtonVariant.primary}
              loading={isSigningOut}
              disabled={isSigningOut}
              onClick={() => void handleConfirmSignOut()}
            />
          </div>
        </div>
      </DefaultDialog>

      {children}
    </>
  )
}
